// @flow
import { Box, Button, CircularProgress, Container, CssBaseline, Grid, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Redirect, useLocation } from 'react-router-dom';
import routes from '../routes';
import Copyright from '../component/Copyright';
import { AuthContext } from '../context/auth';
import { isNotValidPassword } from '../util/validator';
import ApiResponse from '../model/ApiResponse';

const baseUrl = process.env.PUBLIC_URL || '';

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  error: {
    color: theme.palette.error.main,
  },
}));

const ChangePassword = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const { doPost, setToken } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const match = search.match(/token=(.*)/);
  const token = (!!match && match.length > 1) ? match[1] : null;

  const validate = values => {
    const errors = {};
    if (!values.password) {
      errors.password = t('Required');
    } else if (isNotValidPassword(values.password)) {
      errors.password = t('Invalid password');
    }
    return errors;
  };

  const onSubmit = async (values, { setSubmitting }) => {
    const data = {
      ...values,
      email: token,
    };
    const response: ApiResponse = await doPost('/user/change-password', data);
    if (response.ok && response.data) {
      await setToken(response.data);
    } else {
      enqueueSnackbar(t(response.message), {
        variant: 'error',
      });
      setSubmitting(false);
    }
  };

  if (!token) {
    return <Redirect to={'/'}/>;
  }

  return (
    <Container component="main" maxWidth="sm">
      <CssBaseline/>
      <div className={classes.paper}>
        <img src={`${baseUrl}/images/logo.png`} alt="" width={40} height={40}/>
        <Typography component="h1" variant="h5">
          {t('Enter your new password')}
        </Typography>
        <Formik
          initialValues={{
            password: '',
          }}
          validate={validate}
          onSubmit={onSubmit}
        >
          {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
            <form className={classes.form} onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    disabled={isSubmitting}
                    variant="outlined"
                    required
                    fullWidth
                    name="password"
                    label={t('Password')}
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.password}
                  />
                  <span className={classes.error}>{errors.password && touched.password && errors.password}</span>
                </Grid>
              </Grid>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
                type="submit"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress color="secondary" size="1.2em"/> : null}
              >
                {t('Change password')}
              </Button>
              <Grid container justify="flex-end">
                <Grid item>
                  {!isSubmitting && <Link to={routes.signIn}>
                    {t('Already a customer? Sign in')}
                  </Link>}
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </div>
      <Box mt={5}>
        <Copyright/>
      </Box>
    </Container>
  );
};

export default ChangePassword;
